/* ============================================================================
 * variant-picker.js
 *
 * Vanilla <variant-picker> custom element. Step-8 chunk-2C replacement for
 * the (currently broken) Slate variant selection that the comment in
 * snippets/product-form.liquid noted:
 *
 *   "variant button JS is currently broken (theme.Product was removed in
 *    the cleanup pass). Push B will add a variant-picker custom element."
 *
 * Markup contract (rendered by snippets/product-form.liquid):
 *
 *   <variant-picker
 *     data-section-id="{{ section_id }}"
 *     data-url="{{ product.url }}">
 *
 *     <script type="application/json" data-vp-variants>[ ...variants JSON ]</script>
 *
 *     <!-- Option groups (one per option). Buttons inside have data-vp-option-value -->
 *     <div class="product-form__option" data-vp-option-group data-vp-option-name="Color">
 *       <button data-vp-option-value value="Red" class="active">Red</button>
 *       <button data-vp-option-value value="Blue">Blue</button>
 *     </div>
 *     <!-- … more option groups … -->
 *   </variant-picker>
 *
 * Variant JSON shape (one entry per product.variants):
 *
 *   {
 *     "id":                         numeric variant id,
 *     "available":                  boolean,
 *     "options":                    [String]   // option values in product.options order
 *     "price":                      cents,
 *     "price_formatted":            "$24.99",
 *     "compare_at_price_formatted": "$29.99" | null,
 *     "on_sale":                    boolean,
 *     "inventory_quantity":         numeric | null,
 *     "featured_media_id":          numeric | null
 *   }
 *
 * Side effects on variant change (the element does these in-place, no
 * separate listeners required):
 *   - Update active classes on its own buttons
 *   - Update the form's hidden <select name="id"> to the new variant id
 *   - Update [data-cart-add] button: data-cart-add attr + ATC/sold-out text
 *   - Re-render the matching <dl data-price> with the variant's price
 *   - history.replaceState to ?variant=ID so URL-share / refresh preserves
 *
 * Dispatched events (so other code can react — e.g. image gallery in 2E):
 *   - 'variant:change' (bubbles)
 *       detail: { variant, options, productHandle }
 *
 * Attribute namespace: data-vp-* (NOT data-variant-*) so the dead variant
 * code still present in app.js can never bind to these elements even if it
 * gets resurrected.
 * ========================================================================= */

(function () {
  'use strict';

  if (customElements.get('variant-picker')) return;

  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this._onClick = this._onClick.bind(this);
      this._onChange = this._onChange.bind(this);
    }

    connectedCallback() {
      // Variant data is rendered inline so we don't need a network call to
      // do anything.
      var dataEl = this.querySelector('script[type="application/json"][data-vp-variants]');
      if (!dataEl) {
        console.warn('[variant-picker] missing variant data script');
        return;
      }
      try {
        this.variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('[variant-picker] failed to parse variant data', e);
        return;
      }

      this.sectionId = this.getAttribute('data-section-id') || '';
      this.productUrl = this.getAttribute('data-url') || window.location.pathname;

      // Form contract — the hidden select + ATC button + price block live
      // OUTSIDE this element (siblings inside the same <form>). Cache them
      // by querying up from the picker to its containing form.
      this.form = this.closest('form');
      this.hiddenSelect = this.form && this.form.querySelector('select[name="id"]');
      this.atcButton = this.form && this.form.querySelector('[data-cart-add]');
      this.atcText = this.form && this.form.querySelector('[data-add-to-cart-text]');
      // Price block can be outside the form (in the price block on the PDP).
      // We look up to the nearest section so we find it whether it's inside
      // or outside the form.
      var section = this.closest('[data-section-id]') || document;
      this.priceBlock = section.querySelector('[data-price]');

      // Read current selection from .active button per option group.
      this.currentOptions = this._readCurrentOptions();
      this.currentVariant = this._matchVariant(this.currentOptions) ||
        this.variants.find(function (v) { return v.available; }) ||
        this.variants[0];

      // Wire events. Click for the button style, change for the dropdown
      // style — markup uses one or the other, never both.
      this.addEventListener('click', this._onClick);
      this.addEventListener('change', this._onChange);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('change', this._onChange);
    }

    _readCurrentOptions() {
      var options = [];
      var groups = this.querySelectorAll('[data-vp-option-group]');
      groups.forEach(function (group) {
        var active = group.querySelector('[data-vp-option-value].active') ||
          group.querySelector('select[data-vp-option-select]');
        if (active) {
          if (active.tagName === 'SELECT') {
            options.push(active.value);
          } else {
            options.push(active.getAttribute('value'));
          }
        }
      });
      return options;
    }

    _matchVariant(options) {
      return this.variants.find(function (v) {
        return v.options.length === options.length &&
          v.options.every(function (val, i) { return val === options[i]; });
      });
    }

    _onClick(e) {
      var btn = e.target.closest('[data-vp-option-value]');
      if (!btn || btn.tagName !== 'BUTTON') return;
      if (btn.hasAttribute('disabled')) return;
      e.preventDefault();

      var group = btn.closest('[data-vp-option-group]');
      if (!group) return;

      // Toggle .active within the group.
      group.querySelectorAll('[data-vp-option-value]').forEach(function (b) {
        b.classList.remove('active');
      });
      btn.classList.add('active');

      this._afterSelectionChange();
    }

    _onChange(e) {
      var select = e.target.closest('select[data-vp-option-select]');
      if (!select) return;
      this._afterSelectionChange();
    }

    _afterSelectionChange() {
      this.currentOptions = this._readCurrentOptions();
      var matched = this._matchVariant(this.currentOptions);
      this.currentVariant = matched || null;

      this._syncHiddenSelect();
      this._syncATC();
      this._syncPrice();
      this._syncUrl();

      this.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: {
          variant: this.currentVariant,
          options: this.currentOptions.slice(),
          form: this.form
        }
      }));
    }

    _syncHiddenSelect() {
      if (!this.hiddenSelect || !this.currentVariant) return;
      this.hiddenSelect.value = String(this.currentVariant.id);
    }

    _syncATC() {
      if (!this.atcButton) return;
      var v = this.currentVariant;
      if (!v) {
        this.atcButton.setAttribute('disabled', 'disabled');
        if (this.atcText) this.atcText.textContent = window.theme && window.theme.strings && window.theme.strings.unavailable || 'Unavailable';
        return;
      }
      this.atcButton.setAttribute('data-cart-add', String(v.id));
      if (v.available) {
        this.atcButton.removeAttribute('disabled');
        if (this.atcText) {
          this.atcText.textContent = window.theme && window.theme.strings && window.theme.strings.addToCart || 'Add to Cart';
        }
      } else {
        this.atcButton.setAttribute('disabled', 'disabled');
        if (this.atcText) {
          this.atcText.textContent = window.theme && window.theme.strings && window.theme.strings.soldOut || 'Sold out';
        }
      }
    }

    _syncPrice() {
      if (!this.priceBlock || !this.currentVariant) return;
      var v = this.currentVariant;

      // Toggle outer-state classes the existing CSS uses
      this.priceBlock.classList.toggle('price--sold-out', !v.available);
      this.priceBlock.classList.toggle('price--on-sale', !!v.on_sale);

      var regular = this.priceBlock.querySelector('[data-regular-price]');
      var sale = this.priceBlock.querySelector('[data-sale-price]');

      if (v.on_sale && v.compare_at_price_formatted) {
        if (regular) regular.textContent = v.compare_at_price_formatted;
        if (sale) sale.textContent = v.price_formatted;
      } else {
        if (regular) regular.textContent = v.price_formatted;
        if (sale) sale.textContent = v.price_formatted;
      }
    }

    _syncUrl() {
      if (!this.currentVariant) return;
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', String(this.currentVariant.id));
        window.history.replaceState({}, '', url.toString());
      } catch (e) { /* old browsers — skip */ }
    }
  }

  customElements.define('variant-picker', VariantPicker);
})();
